import React, { useState } from "react";
import { Heart, Cookie, Candy, Rabbit, Cat, Bird, PartyPopper } from "lucide-react";
import UploadTab from "./UploadTab";
import DatabaseTab from "./DatabaseTab";
import "./LabCost.css";

const CardLabCost: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"upload" | "database">("upload");

  return (
    <div
      className="lab-cost-root flex w-full overflow-hidden bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 font-sans text-gray-700 rounded-[2.5rem] shadow-2xl border-t border-b border-l border-r border-pink-100/50"
    >
      {/* Left Sidebar */}
      <aside className="w-64 bg-white/80 backdrop-blur-md border-r border-pink-100 flex flex-col shadow-lg z-10">
        {/* Logo Area */}
        <div className="p-8 flex items-center gap-3">
          <div className="bg-gradient-to-tr from-pink-400 to-rose-400 p-2 rounded-2xl shadow-lg rotate-3 hover:rotate-12 transition-transform duration-300">
            <Rabbit className="w-8 h-8 text-white animate-bounce" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-500 tracking-tight">
              Lab Cost
            </h1>
            <span className="text-xs font-bold text-pink-400 tracking-wider flex items-center gap-1">
              AI CUTIE <Heart className="w-3 h-3 fill-pink-400" />
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-3 mt-4">
          <button
            onClick={() => setActiveTab("upload")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group font-bold ${
              activeTab === "upload"
                ? "bg-gradient-to-r from-pink-200 to-rose-200 text-pink-700 shadow-md scale-105"
                : "text-gray-500 hover:bg-pink-100 hover:text-pink-500 hover:scale-105"
            }`}
          >
            <Cookie
              className={`w-6 h-6 transition-transform group-hover:rotate-12 ${activeTab === "upload" ? "fill-pink-400 text-pink-700" : ""}`}
            />
            <span>Upload PDF</span>
            {activeTab === "upload" && (
              <div className="ml-auto w-2 h-2 rounded-full bg-pink-500 animate-ping" />
            )}
          </button>

          <button
            onClick={() => setActiveTab("database")}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group font-bold ${
              activeTab === "database"
                ? "bg-gradient-to-r from-purple-200 to-indigo-200 text-purple-700 shadow-md scale-105"
                : "text-gray-500 hover:bg-purple-100 hover:text-purple-500 hover:scale-105"
            }`}
          >
            <Candy
              className={`w-6 h-6 transition-transform group-hover:rotate-12 ${activeTab === "database" ? "fill-purple-400 text-purple-700" : ""}`}
            />
            <span>Database</span>
            {activeTab === "database" && (
              <div className="ml-auto w-2 h-2 rounded-full bg-purple-500 animate-ping" />
            )}
          </button>
        </nav>

        {/* Footer / Credits */}
        <div className="p-6 text-center text-xs text-gray-400">
          <div className="mb-2 flex justify-center">
            <Heart className="w-4 h-4 text-pink-300 animate-pulse" fill="#F9A8D4" />
          </div>
          <p>© 2024 Lab Cost AI</p>
          <p className="opacity-60 mt-1">Powered by Ollama</p>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto relative">
        {/* Floating Cute Background Elements */}
        <div className="absolute top-10 right-10 opacity-20 pointer-events-none animate-bounce delay-1000">
          <Cookie className="w-24 h-24 text-pink-400 rotate-12" />
        </div>
        <div className="absolute bottom-20 left-20 opacity-20 pointer-events-none animate-bounce delay-700">
          <Candy className="w-16 h-16 text-purple-400 -rotate-12" />
        </div>
        <div className="absolute top-1/2 right-1/4 opacity-10 pointer-events-none animate-pulse">
          <Rabbit className="w-32 h-32 text-rose-300" />
        </div>
        <div className="absolute bottom-10 right-20 opacity-20 pointer-events-none animate-bounce delay-500">
          <Cat className="w-20 h-20 text-indigo-300 rotate-6" />
        </div>
        <div className="absolute top-20 left-1/3 opacity-15 pointer-events-none animate-pulse delay-200">
          <Bird className="w-16 h-16 text-blue-300 -rotate-12" />
        </div>
        <div className="absolute bottom-1/3 right-10 opacity-10 pointer-events-none">
          <PartyPopper className="w-24 h-24 text-yellow-300" />
        </div>

        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white/60 to-transparent pointer-events-none" />

        <div className="w-full mx-auto px-8 py-10 pb-20">
          {activeTab === "upload" ? (
            <div className="animate-lab-cost-fade-in-up">
              <header className="mb-8">
                <h2 className="text-3xl font-bold text-gray-700 mb-2">Upload Quotation</h2>
                <p className="text-gray-400">
                  Transform your PDF price lists into structured data.
                </p>
              </header>
              <UploadTab />
            </div>
          ) : (
            <div className="animate-lab-cost-fade-in-up">
              <header className="mb-8">
                <h2 className="text-3xl font-bold text-gray-700 mb-2">Database Manager</h2>
                <p className="text-gray-400">View, search, and manage your extracted lab tests.</p>
              </header>
              <DatabaseTab />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CardLabCost;
